import React, { FC } from 'react';
import classNames from 'classnames';
import { HoldingRowData } from '../dataTable/DataTable.d';
import DataTable from '../dataTable/DataTable';
import {
  TABLE_HEADING_PERFORMANCE,
  TABLE_HEADING_PERFORMANCE_LIST
} from '../../utils/constants';
import { standardDateFormat } from '../../utils/dateFormatter';
import { formatPercentageChange } from '../../utils/numberFormatter';
import { isEmpty } from 'lodash';
import style from './PerformanceTable.module.scss';
import PastPerformanceStatement from '../styled/PastPerformanceStatement';
import TotalReturnStatement from '../styled/TotalReturnStatement';
import NumericChange from '../shared/NumericChange';

type Props = {
  selectedAccount: AssetAccount;
};

const transformPerformance = (
  portfolio: ReturnsPortfolio,
  rowClass = '',
  customName?: string
) => {
  if (!portfolio) {
    return {};
  }
  const tableData: HoldingRowData = {
    rowClass,
    [TABLE_HEADING_PERFORMANCE.NAME]: customName || portfolio.portfolioName
  };

  if (portfolio.returns) {
    portfolio.returns.forEach((r) => {
      tableData[r.timePeriod.toLowerCase()] = (
        <NumericChange
          formatFunction={formatPercentageChange}
          value={r.returnPercentage}
        />
      );
    });
  }

  return tableData;
};

const transformBenchmarks = (benchmarks) =>
  benchmarks.map((b) => {
    const data = transformPerformance(b, style['benchmark-row']);
    data[TABLE_HEADING_PERFORMANCE.NAME] =
      `Benchmark — ${data[TABLE_HEADING_PERFORMANCE.NAME]}`;
    return data;
  });

const customPerformanceDisplay = (performance) => {
  return (
    !isEmpty(performance.returnPercentage) &&
    Math.abs(parseFloat(performance.returnPercentage)) > 0.00001
  );
};

const sansSinceInception = (r) => {
  return r.timePeriod !== 'SINCE INCEPTION';
};

const PerformanceTable: FC<Props> = ({ selectedAccount }) => {
  const { performances } = selectedAccount.monthlyReport.performance;

  const performanceData = performances.reduce(
    (perfList: HoldingRowData[], performanceSet) => {
      const {
        accountPerformance,
        equity: equityPerformance,
        fixedIncome: fixedIncomePerformance,
        benchmarkPerformances,
        type
      } = performanceSet;

      if (type === 'Gross') {
        if (accountPerformance && accountPerformance !== null) {
          const accountBenchmarks: ReturnsPortfolio[] = [];
          if (benchmarkPerformances) {
            benchmarkPerformances.forEach((benchmark) => {
              accountBenchmarks.push(benchmark);
            });
          }

          const listOfReturns = accountPerformance.returns.filter(
            customPerformanceDisplay
          );
          const removeSinceInception = listOfReturns.filter(
            (r) => r.timePeriod === 'TEN YEAR'
          ).length;

          const customPerformance = {
            portfolioName: accountPerformance.portfolioName,
            returns: removeSinceInception
              ? listOfReturns.filter(sansSinceInception)
              : listOfReturns
          };

          const accountData = transformPerformance(
            customPerformance,
            classNames(style['account-row'], { [style['top-row']]: true })
          );

          perfList.push({
            ...accountData
          });

          transformBenchmarks(accountBenchmarks).forEach((b) =>
            perfList.push(b)
          );
        }

        if (equityPerformance && equityPerformance !== null) {
          const equityBenchmarks: any[] = [];
          if (benchmarkPerformances) {
            benchmarkPerformances.forEach((benchmark) => {
              equityBenchmarks.push(benchmark);
            });
          }

          const equityData = transformPerformance(
            equityPerformance,
            classNames(style['account-row']),
            'EQUITIES'
          );

          perfList.push({
            ...equityData
          });

          transformBenchmarks(equityBenchmarks).forEach((b) =>
            perfList.push(b)
          );
        }

        if (fixedIncomePerformance && fixedIncomePerformance !== null) {
          const fixedIncomeBenchmarks: any[] = [];
          if (benchmarkPerformances) {
            benchmarkPerformances.forEach((benchmark) => {
              fixedIncomeBenchmarks.push(benchmark);
            });
          }

          const fixedIncomeData = transformPerformance(
            fixedIncomePerformance,
            classNames(style['account-row']),
            'FIXED INCOME'
          );

          perfList.push({
            ...fixedIncomeData
          });

          transformBenchmarks(fixedIncomeBenchmarks).forEach((b) =>
            perfList.push(b)
          );
        }
      }
      return perfList;
    },
    []
  ); // tslint:disable-line align
  const { asOfDate } = selectedAccount.monthlyReport;

  const performanceDataKeys = Object.keys(performanceData['0']);

  const TABLE_HEADING_PERFORMANCE_LIST_LC = TABLE_HEADING_PERFORMANCE_LIST.map(
    (e) => e.toLowerCase()
  );

  const TABLE_HEADING_PERFORMANCE_LIST_MATCH =
    performanceDataKeys.filter(filterTimePeriods);
  TABLE_HEADING_PERFORMANCE_LIST_MATCH.unshift(' ');

  function filterTimePeriods(el: string) {
    if (el) {
      return TABLE_HEADING_PERFORMANCE_LIST_LC.indexOf(el) > 0;
    }
    return null;
  }

  const index = TABLE_HEADING_PERFORMANCE_LIST_MATCH.findIndex(
    (x) => x === 'year-to-date'
  );
  const index1 = TABLE_HEADING_PERFORMANCE_LIST_MATCH.findIndex(
    (x) => x === 'one year'
  );
  const index2 = TABLE_HEADING_PERFORMANCE_LIST_MATCH.findIndex(
    (x) => x === 'three year'
  );
  const index3 = TABLE_HEADING_PERFORMANCE_LIST_MATCH.findIndex(
    (x) => x === 'five year'
  );
  const index4 = TABLE_HEADING_PERFORMANCE_LIST_MATCH.findIndex(
    (x) => x === 'ten year'
  );
  const index5 = TABLE_HEADING_PERFORMANCE_LIST_MATCH.findIndex(
    (x) => x === 'since inception'
  );

  TABLE_HEADING_PERFORMANCE_LIST_MATCH[index] = 'Year-to-Date';
  TABLE_HEADING_PERFORMANCE_LIST_MATCH[index1] = 'One Year';
  TABLE_HEADING_PERFORMANCE_LIST_MATCH[index2] = 'Three Year';
  TABLE_HEADING_PERFORMANCE_LIST_MATCH[index3] = 'Five Year';
  TABLE_HEADING_PERFORMANCE_LIST_MATCH[index4] = 'Ten Year';
  TABLE_HEADING_PERFORMANCE_LIST_MATCH[index5] = 'Since Inception';

  const columnClasses = {
    [TABLE_HEADING_PERFORMANCE.NAME]: style['name-column']
  };

  return (
    <div className={style['table-container']}>
      <div className={style['title-section']}>
        <span className={style['table-title']}>Portfolio Performance</span>
        <span className={style['table-date']}>
          As of {standardDateFormat(asOfDate)}
        </span>
      </div>
      <DataTable
        headers={TABLE_HEADING_PERFORMANCE_LIST_MATCH}
        rowData={performanceData}
        columnClasses={columnClasses}
        tableFormatClass={style['performance-table-layout']}
        selectedAccount={selectedAccount}
        labelHeader={TABLE_HEADING_PERFORMANCE.NAME}
        valueHeader={TABLE_HEADING_PERFORMANCE.YEAR_TO_DATE}
      />
      <div className={style['disclaimer']}>
        <PastPerformanceStatement>
          <TotalReturnStatement />
        </PastPerformanceStatement>
        <p>
          All returns shown, including those greater than 1 year, are
          cumulative. Returns shown with gross dividends reinvested.
        </p>
      </div>
    </div>
  );
};

export default PerformanceTable;
